import React, { ChangeEvent, useRef, useState } from "react";
import { Box, Button, Typography, IconButton, useTheme } from "@mui/material";
import { StyledBox, StyledUpoadBox } from "../../../styled";
import UploadedImage from "./UploadedImage";
import { TYPE_UPLOAD_MODAL } from "../../../constants";
import { openModal } from "../../../store/slice/UISlice";
import VisionStudioSvg from "../../VisionStudioSvg";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchImagesNew } from "../../../store/slice/ProjectImagesUploadedSlice";

import fileUploadIcon from "../../../assets/img/file_upload.svg";

interface ImageUploadProps {
  onClose: () => void; // Define the type for onClose
  taskType?: string;
}

const ManualImageUploadType: React.FC<ImageUploadProps> = ({
  onClose,
  taskType,
}) => {
  const count = 40;
  interface Params extends Record<string, string | undefined> {
    id: string; // 'id' is expected to be a string
  }
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dispatch: AppDispatch = useDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const [imageSrcs, setImageSrcs] = useState<string[]>([]);
  const theme = useTheme();
  const { id } = useParams<Params>();
  const [progress, setProgress] = useState(0);
  const handleClickOpen = () => {
    dispatch(openModal({ name: TYPE_UPLOAD_MODAL, taskType: taskType }));
  };
  const handleIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Trigger the hidden input's click
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLMediaElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();

    const clearFileInput = () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    try {
      let selectedFiles: File[] = [];
      let ignoredFilesCount = 0;
      let invalidFilesCount = 0;

      const validExtensions = ["png", "jpg", "jpeg"];

      if (event.type === "drop") {
        const droppedFiles = Array.from(
          (event as any).dataTransfer?.files ?? []
        );
        selectedFiles = (droppedFiles as File[]).filter((file) => {
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          if (!fileExtension || !validExtensions.includes(fileExtension)) {
            invalidFilesCount++;
            return false;
          }

          if (file.size > 5 * 1024 * 1024) {
            ignoredFilesCount++;
            return false;
          }
          return true;
        });
      } else {
        const inputFiles = Array.from(
          (event.currentTarget as HTMLInputElement).files ?? []
        );
        selectedFiles = (inputFiles as File[]).filter((file) => {
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          if (!fileExtension || !validExtensions.includes(fileExtension)) {
            invalidFilesCount++;
            return false;
          }
          if (file.size > 5 * 1024 * 1024) {
            ignoredFilesCount++;
            return false;
          }
          return true;
        });
      }

      if (invalidFilesCount) {
        clearFileInput();
        toast.error(
          `${invalidFilesCount} file${invalidFilesCount === 1 ? "" : "s"
          } have invalid format. Only png, jpg, and jpeg are allowed.`
        );
      }

      if (ignoredFilesCount) {
        clearFileInput();
        toast.error(
          `${ignoredFilesCount} image${ignoredFilesCount === 1 ? "" : "s"
          } exceeded the 5 MB`
        );
      }

      if (selectedFiles.length > 0) {
        const newImageSrcs: string[] = [];
        const newFiles: File[] = [];
        const formData = new FormData();

        // Append other form data
        if (id) {
          formData.append("ProjectId", id);
        } else {
          toast.error("ProjectId is undefined");
          return; // Exit if ProjectId is not defined
        }

        formData.append("Id", "988");

        for (const file of selectedFiles) {
          newFiles.push(file);
          const reader = new FileReader();

          reader.onloadend = async () => {
            newImageSrcs.push(reader.result as string);
            formData.append("Images", file);

            if (newImageSrcs.length === selectedFiles.length) {
              const totalSize = newFiles.reduce(
                (acc, file) => acc + file.size,
                0
              );

              if (totalSize > 300 * 1024 * 1024) {
                clearFileInput();
                toast.error("Total file size exceeds the 300 MB limit!");
                return; // Stop execution if size limit is exceeded
              }

              setImageSrcs(newImageSrcs);
              setFiles(newFiles);

              // Create a new XMLHttpRequest for uploading images
              const xhr = new XMLHttpRequest();
              xhr.open(
                "POST",
                `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/Upload`
              );
              const token = localStorage.getItem("token");
              xhr.setRequestHeader("Authorization", `${token}`);

              xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                  const percentComplete = (event.loaded / event.total) * 100;
                  const onlyInteger = Math.floor(percentComplete);

                  if (onlyInteger < 100) {
                    setProgress(onlyInteger);
                  }
                }
              };

              // Handle success
              xhr.onload = () => {
                if (xhr.status === 200) {
                  toast.success(
                    `Image${JSON.parse(xhr.response).length === 1 ? "" : "s"
                    } uploaded successfully!`
                  );
                  setProgress(100);
                  dispatch(
                    fetchImagesNew({
                      projectId: id,
                      page: 1,
                      count: count,
                      annotation: "all",
                    })
                  )
                    .unwrap()
                    .then((response) => {
                      if (response.imageUrlModel.length === 0) {
                        dispatch(openModal({ name: TYPE_UPLOAD_MODAL }));
                      }
                    });
                  const parentElement = document.querySelector(".parent-class");
                  if (parentElement) {
                    parentElement.scrollTo({ top: 0, behavior: "smooth" });
                  }

                  onClose();
                } else {
                  let errorMessage: string;

                  if (typeof xhr.response === "string") {
                    errorMessage = xhr.response;
                  } else if (xhr.response && xhr.response.detail) {
                    errorMessage = xhr.response.detail;
                  } else {
                    errorMessage = "Failed to upload images.";
                  }

                  console.error("Error Response:", xhr.response);
                  toast.error(errorMessage);
                  onClose();
                }
              };

              // Handle error
              xhr.onerror = () => {
                console.error("Error uploading images:", xhr.response);
                toast.error(xhr.response);
              };

              xhr.send(formData);
            }
          };
          reader.readAsDataURL(file);
        }
      }
    } catch (error) {
      console.error(error, "error");
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: theme.typography.h6.fontSize,
          fontWeight: theme.typography.h5.fontWeight,
          position: "absolute",
          top: "10px",
        }}
      >
        Upload Images
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          position: "absolute",
          right: "55px",
          top: "10px",
        }}
      >
        {progress === 0 && (
          <Button
            id="backButton"
            onClick={handleClickOpen}
            variant="contained"
            color="primary"
            sx={{ ml: 2 }}
          >
            ←
          </Button>
        )}
      </Box>
      <StyledBox sx={{ backgroundColor: "#FFFFFF", marginTop: "20px" }}>
        {imageSrcs.length === 0 && (
          <StyledUpoadBox
            onDragOver={(e: any) => {
              e.preventDefault(); // Prevent default to allow drop
              e.stopPropagation(); // Stop bubbling
            }}
            onDrop={(e: any) => {
              e.preventDefault();
              e.stopPropagation(); // Stop bubbling
              if (e.dataTransfer?.files?.length) {
                handleFileChange(e as unknown as ChangeEvent<HTMLInputElement>);
              }
            }}
            id="uploadImageOnly"
            sx={{
              display: "flex",
              margin: "0 56px",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-evenly",
              cursor: "pointer",
            }}
          >
            {/* Pre-set Data Side */}
            <Box
              onClick={(e) => {
                e.stopPropagation(); // Stop bubbling
                handleIconClick();
              }}
              sx={{
                textAlign: "center",
                flex: "0 0 99%",
                padding: "3rem 0",
              }}
            >
              <Box>
                <IconButton>
                  <VisionStudioSvg icon={fileUploadIcon} />
                  <input
                    hidden
                    accept="image/*"
                    ref={fileInputRef}
                    type="file"
                    onChange={(e) =>
                      handleFileChange(
                        e as unknown as ChangeEvent<HTMLInputElement>
                      )
                    }
                  />
                </IconButton>
              </Box>
              <Box display="flex" width="100%" justifyContent="center">
                <Typography
                  variant="h6"
                  marginTop="0px"
                  sx={{
                    fontSize: "12px",
                    color: "#132A00",
                  }}
                >
                  Drop your image here, or
                  <Button
                    component="label"
                    sx={{
                      fontSize: "12px",
                      textTransform: "initial",
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent bubbling
                  >
                    browse
                    <input
                      hidden
                      accept="image/*"
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={(e) =>
                        handleFileChange(
                          e as unknown as ChangeEvent<HTMLInputElement>
                        )
                      }
                    />
                  </Button>
                </Typography>
              </Box>
            </Box>
          </StyledUpoadBox>
        )}
        {imageSrcs.length > 0 && (
          <Box
            sx={{
              border: "1px solid #E4E9F0",
              padding: "12px",
              borderRadius: "12px",
              width: "96%",
            }}
          >
            <UploadedImage
              imageUrl={imageSrcs}
              files={files}
              progress={progress}
            />
          </Box>
        )}
      </StyledBox>
    </Box>
  );
};

export default ManualImageUploadType;
